import React, { useCallback, useEffect } from 'react';
import Form, { FormInstance } from 'antd/lib/form';

import { useClientFormContext } from './ClientFormContext';
import SubmitButton from '../../components/Client/ClientFormSubmitButton';
import { CLIENT_STATUS } from '../../types/Client';
import ToastNotification from '../../utils/ToastNotification';

interface ClientFormProps {
    name: string;
    children: React.FC<{ form: FormInstance }>;
    tabIsLoadingData?: boolean;
    submitForm?: undefined | ((form: FormInstance) => Promise<void>) | ((form: FormInstance) => Promise<unknown>);
}

const submitContainerStyles = {
    bottom: '0px',
    right: '0px',
};

const ClientForm: React.FC<ClientFormProps> = ({
    children,
    tabIsLoadingData,
    name,
    submitForm,
}: ClientFormProps): React.ReactElement => {
    const [form] = Form.useForm();
    const { client, clientId, formConfig, isLoadingData, mode, resetForm } = useClientFormContext();
    useEffect(() => {
        resetForm(form);
    }, [mode, clientId]);

    const formName = `${mode}-client-form-${name}`;

    const renderSubmitButton = (): JSX.Element | null => {
        if (
            (formConfig.isAddMode || formConfig.isEditMode) &&
            !(isLoadingData || tabIsLoadingData) &&
            client?.status !== CLIENT_STATUS.DELETED
        ) {
            return <SubmitButton form={form} formId={formName} />;
        }
        return null;
    };

    const submitFormMethod = useCallback(async () => {
        if (submitForm) {
            await submitForm(form);
        } else {
            ToastNotification({
                type: 'error',
                message: 'error',
                description: 'Could not submit form...',
            });
        }
    }, [form, submitForm]);

    return (
        <Form
            form={form}
            name={formName}
            id={formName}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            onFinish={(): Promise<void> => submitFormMethod()}
        >
            {children({ form })}
            <div className="position-relative w-100 text-right my-3" style={submitContainerStyles}>
                {renderSubmitButton()}
            </div>
        </Form>
    );
};

export default ClientForm;
